import { CalendarClock, ExternalLink, FileSignature, PhoneOutgoing, UserPlus } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { NewCaseDialog } from "@/features/accident-person";
import { BookCallbackDialog } from "@/features/book-callback";
import { SendContractDialog } from "@/features/send-contract";
import {
  agreementText, crashLine, otherCasesOf, partiesOf, partyCallbackText, partyNumberText, roleText,
} from "@/lib/accident";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { callbackWhen, knownZone } from "@/lib/callbacks";
import { caseLabel } from "@/lib/cases";
import { contactName, stageText } from "@/lib/contacts";
import { useContact, useContactFields, useContractCheck } from "@/lib/queries";
import { fieldText } from "@/lib/record-fields";
import type { AccidentRecord, LeadAccident, LeadCard } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { useOutboundDial } from "@/softphone/manual-call";

/**
 * The accident's people on the call's card (ACL-671, ADR-226 in the platform repo): everyone in the
 * caller's accident, where each of them stands, and what the rep does next for each, without leaving
 * the phone page.
 *
 * One row a person: the caller's own first, then the others a person put in the accident, as the
 * accident route lists them with their number, agreement, callback and dial-list standing. A row
 * carries Call now (between calls only: on a call the phone has no second line to dial on), Book a
 * callback (the person's own Book a callback, as their record offers it, ACL-657), Send agreement (the
 * person's own Send contract) and Open. The caller's row carries the page's own Send contract
 * (`ownActions`), drawn as the other rows' Send agreement is (`ROW_ACTION`).
 * Add a party/case at the foot is the New case dialog (ADR-220), titled by the accident picked; the
 * caller's other cases are one line under it.
 *
 * The dialogs for another person read that person's record when they open, not before: the card
 * asks for nothing on a call that stays with the caller.
 */
export function AccidentParties({
  card, accident, campaignId, onCall, ownActions,
}: {
  card: LeadCard;
  accident: LeadAccident;
  /** The campaign a Call now is placed through: the call's line's, else the caller's calling campaign. */
  campaignId: string | null;
  /** A call is up: there is no line to call another person on, so Call now waits. */
  onCall: boolean;
  /** The page's actions for the caller's own row (Send contract on the Phone page, ACL-560). */
  ownActions?: React.ReactNode;
}) {
  const { user } = useAuth();
  const [adding, setAdding] = React.useState(false);
  const own = accident.accident;
  const parties = partiesOf(accident);
  const fields = useContactFields(card.id);
  const crash = crashLine(fields.data, fieldText);
  const others = otherCasesOf(card);
  if (!own) return null;
  const zone = knownZone(card.clock_timezone ?? card.timezone);
  const when = (iso: string) => callbackWhen(iso, zone).there;
  const me = user?.id ?? null;
  const name = contactName(card) ?? formatPhoneNumber(card.phone);
  const first = card.first_name ?? name;
  const people = parties.length + 1;

  return (
    <section aria-label="This accident" className="mt-2 border-t border-border pt-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-2xs font-semibold uppercase tracking-wide text-text-subtle">
          Accident {own.number || ""}
        </p>
        <Badge tone="neutral" size="sm">
          {people} {people === 1 ? "person" : "people"}
        </Badge>
      </div>
      {crash && <p className="mt-0.5 text-2xs text-text-muted">{crash}</p>}
      <ul className="mt-1.5 space-y-1.5" aria-label="In this accident">
        <PartyRow
          id={card.id}
          name={name}
          detail={[roleText(own.role), formatPhoneNumber(card.phone)].filter(Boolean).join(" · ")}
          stage={card.stage}
          agreement={agreementText(accident.agreement, when)}
          callback={
            card.callback && (card.callback.status === "upcoming" || card.callback.status === "due")
              ? partyCallbackText(
                  {
                    id: card.callback.id, due_at: card.callback.due_at, backup_at: card.callback.backup_at ?? null,
                    status: card.callback.status, rep: card.callback.rep, rep_user_id: card.callback.rep_user_id ?? null,
                  },
                  when,
                  me,
                )
              : null
          }
          own
          actions={
            ownActions || !onCall ? (
              <>
                {ownActions}
                {!onCall && <OwnBookCallback card={card} />}
              </>
            ) : undefined
          }
        />
        {parties.map((party) => (
          <Party
            key={party.id}
            party={party}
            caller={card}
            campaignId={campaignId}
            onCall={onCall}
            when={when}
            me={me}
          />
        ))}
      </ul>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
          <UserPlus />
          Add a party/case
        </Button>
      </div>
      {others.length > 0 && (
        <p className="mt-2 text-2xs text-text-muted">
          {first}'s other {others.length === 1 ? "case" : "cases"}:{" "}
          {others.map((s, i) => (
            <React.Fragment key={s.id}>
              {i > 0 && ", "}
              {s.may_open ? (
                <Link to={`/contacts/${s.id}`} className="font-medium text-accent-11 hover:underline">
                  {caseLabel(s)}
                </Link>
              ) : (
                <span>{caseLabel(s)}</span>
              )}
            </React.Fragment>
          ))}
        </p>
      )}
      {adding && (
        // Someone else in this accident is the dialog's own first answer; a new accident makes it New case.
        <NewCaseDialog open onOpenChange={setAdding} source={card} />
      )}
    </section>
  );
}

/** A row's action: a link-styled button, the same on every row, the caller's Send contract included. */
export const ROW_ACTION = "inline-flex items-center gap-1 text-2xs font-medium text-accent-11 hover:underline disabled:opacity-50 disabled:hover:no-underline";

/** Book a callback for the caller themself, between calls: the record's own booking (ACL-671). */
function OwnBookCallback({ card }: { card: LeadCard }) {
  const [booking, setBooking] = React.useState(false);
  return (
    <>
      <button type="button" className={ROW_ACTION} onClick={() => setBooking(true)}>
        <CalendarClock className="size-3" aria-hidden />
        {card.callback && (card.callback.status === "upcoming" || card.callback.status === "due") ? "Change callback" : "Book a callback"}
      </button>
      {booking && <BookCallbackDialog open onOpenChange={(open) => !open && setBooking(false)} contact={card} />}
    </>
  );
}

/** One person's row: who, where they stand, and the actions given. */
function PartyRow({
  id, name, detail, stage, agreement, callback, own = false, actions,
}: {
  id: string;
  name: string;
  detail: string | null;
  stage: string | null;
  agreement: ReturnType<typeof agreementText>;
  callback: string | null;
  own?: boolean;
  actions?: React.ReactNode;
}) {
  const st = stageText(stage);
  return (
    <li
      data-record={id}
      className={cn("rounded-md border px-2 py-1.5", own ? "border-accent-6 bg-accent-2" : "border-border bg-surface")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-text">
            {name}
            {own && <span className="font-normal text-text-muted"> · on this call</span>}
          </p>
          {detail && <p className="truncate text-2xs text-text-muted">{detail}</p>}
        </div>
        <Badge tone={st.tone} size="sm" title={st.description}>
          {st.label}
        </Badge>
      </div>
      <div className="mt-1 flex flex-col gap-0.5 text-2xs">
        <p
          className={cn(
            "flex items-start gap-1",
            agreement.tone === "warning" ? "text-warning-11" : agreement.tone === "success" ? "text-success-11" : "text-text-muted",
          )}
        >
          <FileSignature className="mt-px size-3 shrink-0" aria-hidden />
          <span className="min-w-0">{agreement.text}</span>
        </p>
        {callback && (
          <p className="flex items-start gap-1 text-accent-11">
            <CalendarClock className="mt-px size-3 shrink-0" aria-hidden />
            <span className="min-w-0">{callback}</span>
          </p>
        )}
      </div>
      {actions && <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">{actions}</div>}
    </li>
  );
}

/** Another person in the accident, with the four actions. Their record is read when a dialog opens. */
function Party({
  party, caller, campaignId, onCall, when, me,
}: {
  party: AccidentRecord;
  caller: LeadCard;
  campaignId: string | null;
  onCall: boolean;
  when: (iso: string) => string;
  me: number | null;
}) {
  const toast = useToast();
  const dial = useOutboundDial();
  const [doing, setDoing] = React.useState<"callback" | "agreement" | null>(null);
  const [calling, setCalling] = React.useState(false);
  // Read only for a dialog, and kept once read: the dialog's props are the person's card.
  const record = useContact(doing !== null ? party.id : undefined);
  const check = useContractCheck(doing === "agreement" ? party.id : undefined);
  const name = contactName(party) ?? (party.may_open ? "No name" : "Restricted record");
  const signed = party.agreement?.state === "signed";
  const shared = Boolean(party.phone) && party.phone === caller.phone;

  const callNow = async () => {
    if (!party.phone) return;
    setCalling(true);
    try {
      await dial.place(party.phone, campaignId, party.id);
    } catch (e) {
      toast.error("Could not place the call", e instanceof ApiError ? e.message : "Please try again.");
    } finally {
      setCalling(false);
    }
  };

  return (
    <PartyRow
      id={party.id}
      name={name}
      detail={[roleText(party.role), partyNumberText(party, caller, formatPhoneNumber)].filter(Boolean).join(" · ") || null}
      stage={party.stage}
      agreement={agreementText(party.agreement, when)}
      callback={partyCallbackText(party.callback, when, me)}
      actions={
        party.may_open ? (
          <>
            {!onCall && party.phone && !shared && (
              <button type="button" className={ROW_ACTION} disabled={calling || dial.placing} onClick={() => void callNow()}>
                <PhoneOutgoing className="size-3" aria-hidden />
                Call now
              </button>
            )}
            <button type="button" className={ROW_ACTION} onClick={() => setDoing("callback")}>
              <CalendarClock className="size-3" aria-hidden />
              {party.callback ? "Change callback" : "Book a callback"}
            </button>
            {!signed && (
              <button type="button" className={ROW_ACTION} onClick={() => setDoing("agreement")}>
                <FileSignature className="size-3" aria-hidden />
                {party.agreement?.state === "sent" ? "Resend agreement" : "Send agreement"}
              </button>
            )}
            <Link to={`/contacts/${party.id}`} className={ROW_ACTION}>
              <ExternalLink className="size-3" aria-hidden />
              Open
            </Link>
            {doing === "callback" && record.data && (
              <BookCallbackDialog open onOpenChange={(open) => !open && setDoing(null)} contact={record.data} />
            )}
            {doing === "agreement" && record.data && (
              <SendContractDialog
                open
                onOpenChange={(open) => !open && setDoing(null)}
                contact={record.data}
                check={check.data}
                checking={check.isFetching}
              />
            )}
          </>
        ) : undefined
      }
    />
  );
}
